import { useEffect, useRef, useState } from "react";
import {
  FileAudio,
  FileText,
  ImageIcon,
  Loader2,
  Plus,
  Trash2,
  Undo2,
} from "lucide-react";

import { useGetMediaLimitsQuery, useGetRecordMediaQuery } from "@/api/api";
import {
  HGMediaType,
  HGMediaTypeLimits,
  HGRecordMedia,
} from "@/common/types";
import { cn } from "@/lib/utils";
import { Button } from "../button";
import { FieldLabel } from "../field";

export type PendingMediaStatus = "pending" | "uploading" | "done" | "error";

export interface PendingMediaFile {
  localId: string;
  file: File;
  mediaType: HGMediaType;
  status: PendingMediaStatus;
  error?: string;
}

interface MediaInputProps {
  recordId?: string;
  pending: PendingMediaFile[];
  setPending: React.Dispatch<React.SetStateAction<PendingMediaFile[]>>;
  deletedIds: string[];
  setDeletedIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const SECTIONS: { type: HGMediaType; label: string }[] = [
  { type: "image", label: "Images" },
  { type: "audio", label: "Audio" },
  { type: "document", label: "Documents" },
];

// Shared with AvatarInput, RecordForm and the paste handlers -- kept here
// alongside MediaInput rather than split out, at the cost of this file not
// being a clean Fast Refresh boundary.
// eslint-disable-next-line react-refresh/only-export-components
export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function fileExtension(name: string) {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

// Validates files against a media type's limits and builds PendingMediaFile
// entries. Shared by the file picker and the paste handlers. `alreadyCount` is
// the number of files of this type already present (existing + pending).
// eslint-disable-next-line react-refresh/only-export-components
export function acceptMediaFiles(
  files: File[],
  mediaType: HGMediaType,
  typeLimits: HGMediaTypeLimits | undefined,
  alreadyCount: number,
): { accepted: PendingMediaFile[]; error: string | null } {
  const accepted: PendingMediaFile[] = [];
  let error: string | null = null;

  for (const file of files) {
    if (typeLimits) {
      const ext = fileExtension(file.name);
      if (!typeLimits.allowed_extensions.includes(ext)) {
        error = `.${ext || "?"} files are not allowed`;
        continue;
      }
      if (file.size > typeLimits.max_size_bytes) {
        error = `"${file.name}" exceeds ${formatBytes(typeLimits.max_size_bytes)}`;
        continue;
      }
      if (alreadyCount + accepted.length >= typeLimits.max_count) {
        error = `Limit of ${typeLimits.max_count} files reached`;
        continue;
      }
    }
    accepted.push({
      localId: crypto.randomUUID(),
      file,
      mediaType,
      status: "pending",
    });
  }

  return { accepted, error };
}

function MediaTypeIcon({ mediaType }: { mediaType: HGMediaType }) {
  if (mediaType === "audio") {
    return <FileAudio className="h-4 w-4 shrink-0 text-muted-foreground" />;
  }
  return <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function PendingImagePreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    // Genuine synchronization with an external system (a browser-managed
    // object URL that must be revoked on cleanup) -- there's no pure
    // render-time derivation to fall back to here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUrl(objectUrl);
    setFailed(false);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  // Browsers can't decode some formats locally (e.g. HEIC) — show an icon;
  // the server re-encodes to JPEG on upload
  if (!url || failed) {
    return <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />;
  }
  return (
    <img
      src={url}
      alt={file.name}
      className="h-8 w-8 shrink-0 rounded object-cover"
      onError={() => setFailed(true)}
    />
  );
}

export function MediaInput({
  recordId,
  pending,
  setPending,
  deletedIds,
  setDeletedIds,
}: MediaInputProps) {
  const { data: limits } = useGetMediaLimitsQuery();
  const { data: existing } = useGetRecordMediaQuery(recordId ?? "", {
    skip: !recordId,
  });
  const inputRefs = useRef<Partial<Record<HGMediaType, HTMLInputElement>>>({});
  const [errors, setErrors] = useState<
    Partial<Record<HGMediaType, string | null>>
  >({});

  const pickFiles = (mediaType: HGMediaType, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const existingOfType = (existing ?? []).filter(
      (m) => m.media_type === mediaType && !deletedIds.includes(m.id),
    ).length;
    const pendingOfType = pending.filter(
      (p) => p.mediaType === mediaType,
    ).length;

    const { accepted, error } = acceptMediaFiles(
      Array.from(files),
      mediaType,
      limits?.[mediaType],
      existingOfType + pendingOfType,
    );

    if (accepted.length > 0) {
      setPending((prev) => [...prev, ...accepted]);
    }
    setErrors((prev) => ({ ...prev, [mediaType]: error }));
  };

  const removePending = (localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId));
  };

  const toggleDeleted = (mediaId: string) => {
    setDeletedIds((prev) =>
      prev.includes(mediaId)
        ? prev.filter((id) => id !== mediaId)
        : [...prev, mediaId],
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {SECTIONS.map(({ type, label }) => {
        const typeLimits = limits?.[type];
        const existingOfType = (existing ?? []).filter(
          (m) => m.media_type === type,
        );
        const pendingOfType = pending.filter((p) => p.mediaType === type);
        const error = errors[type];

        return (
          <div key={type} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <FieldLabel className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">
                {label}
              </FieldLabel>
              <Button
                size="icon"
                type="button"
                variant="secondary"
                className="h-[26px] w-[26px] rounded-[7px] bg-accent text-accent-foreground hover:bg-accent/80"
                onClick={() => inputRefs.current[type]?.click()}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
              <input
                ref={(el) => {
                  if (el) inputRefs.current[type] = el;
                }}
                type="file"
                multiple
                className="hidden"
                accept={typeLimits?.allowed_extensions
                  .map((ext) => `.${ext}`)
                  .join(",")}
                onChange={(e) => {
                  pickFiles(type, e.target.files);
                  e.target.value = "";
                }}
              />
            </div>

            {error && (
              <div className="text-[12px] text-destructive">{error}</div>
            )}

            {(existingOfType.length > 0 || pendingOfType.length > 0) && (
              <div className="flex flex-col gap-1">
                {existingOfType.map((media: HGRecordMedia) => {
                  const markedDeleted = deletedIds.includes(media.id);
                  return (
                    <div
                      key={media.id}
                      className={cn(
                        "flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5",
                        markedDeleted && "opacity-50",
                      )}
                    >
                      {media.media_type === "image" && media.thumbnail_url ? (
                        <img
                          src={media.thumbnail_url}
                          alt={media.original_filename}
                          className="h-8 w-8 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <MediaTypeIcon mediaType={media.media_type} />
                      )}
                      <div className="min-w-0 flex-1">
                        <div
                          className={cn(
                            "truncate text-[12.5px] text-foreground",
                            markedDeleted && "line-through",
                          )}
                        >
                          {media.original_filename}
                        </div>
                        <div className="font-mono text-[10.5px] text-muted-foreground">
                          {formatBytes(media.size_bytes)}
                        </div>
                      </div>
                      <Button
                        size="icon"
                        type="button"
                        variant="ghost"
                        className={cn(
                          "shrink-0",
                          markedDeleted
                            ? "text-muted-foreground"
                            : "text-destructive",
                        )}
                        onClick={() => toggleDeleted(media.id)}
                      >
                        {markedDeleted ? (
                          <Undo2 className="h-4 w-4" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  );
                })}

                {pendingOfType.map((p) => (
                  <div
                    key={p.localId}
                    className="flex items-center gap-2 rounded-md border border-dashed border-border px-2 py-1.5"
                  >
                    {p.mediaType === "image" ? (
                      <PendingImagePreview file={p.file} />
                    ) : (
                      <MediaTypeIcon mediaType={p.mediaType} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] text-foreground">
                        {p.file.name}
                      </div>
                      <div className="font-mono text-[10.5px] text-muted-foreground">
                        {formatBytes(p.file.size)}
                        {p.status === "done" && " · uploaded"}
                      </div>
                      {p.status === "error" && (
                        <div className="text-[11px] text-destructive">
                          {p.error || "Upload failed"}
                        </div>
                      )}
                    </div>
                    {p.status === "uploading" ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                    ) : (
                      p.status !== "done" && (
                        <Button
                          size="icon"
                          type="button"
                          variant="ghost"
                          className="shrink-0 text-destructive"
                          onClick={() => removePending(p.localId)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
